import { useState, useEffect, useCallback, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { getEffectiveTimezone, formatUTCToZone } from '../../lib/timezone';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../lib/api';
import { useToken } from '../../hooks/useToken';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Bell } from 'lucide-react';
import DashboardWrapper from '../../layout/DashboardWrapper';
import { toast } from 'sonner';
import { PaginationControls } from '../../components/sections/PaginationControls';
import { DataNotFoundSection } from '../../components/sections/DataNotFoundSection';
import NotificationSkeleton from '../../components/skeletons/NotificationSkeleton';

// Helper function to parse date strings as UTC
const parseNotificationDate = dateString => {
    if (!dateString) return null;
    const hasTZ = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    return new Date(hasTZ ? dateString : dateString + 'Z');
};

// Notification item component will be defined inside NotificationsPage so it can access `user` from hook

const NotificationsPage = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all'); // all, read, unread
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalNotificationsCount, setTotalNotificationsCount] = useState(0); // To store total count
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const statusFilterInitializedRef = useRef(false);
    const { user } = useUser();
    const token = useToken();

    // NotificationItem defined inside component so it can access `user` hook value
    const NotificationItem = ({ notification, onClick, isLast }) => {
        const notificationDate = parseNotificationDate(notification.created_at);
        const tz = getEffectiveTimezone(user);
        return (
            <div
                className={`relative py-3 text-neutral-700 cursor-pointer hover:bg-neutral-300/10 transition-colors ${
                    isLast ? '' : 'border-b border-neutral-300'
                }`}
                onClick={() => onClick(notification.id)}
            >
                <h5 className="pb-1">{notification.title}</h5>
                <p className="text-md">{notification.message}</p>
                <div className="flex justify-between text-neutral-500 text-sm font-semibold">
                    <p className="text-sm">
                        {formatUTCToZone(notificationDate, tz, {
                            weekday: 'long',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                        })}
                    </p>
                    <p className="text-sm">{formatDistanceToNow(notificationDate, { addSuffix: true })}</p>
                </div>
                {!notification.is_read && <div className="bg-primary w-2 h-2 absolute top-2 right-0 rounded-full"></div>}
            </div>
        );
    };

    const fetchNotifications = useCallback(
        async page => {
            if (!user) return;
            try {
                setLoading(true);
                if (!token) return;
                const params = { ...(statusFilter !== 'all' && { status: statusFilter }), page, page_size: itemsPerPage };
                const response = await api.getUserNotifications(token, params);
                setNotifications(response.notifications || []);
                setTotalPages(response.total_pages || 1);
                setTotalNotificationsCount(
                    response.total_count || response.total_items || (response.total_pages || 1) * itemsPerPage
                );
            } catch (error) {
                console.error('Failed to fetch notifications', error);
                setNotifications([]);
                setTotalPages(1);
                setTotalNotificationsCount(0);
            } finally {
                setLoading(false);
            }
        },
        [user, statusFilter, token, itemsPerPage]
    );

    // Set document title once when the user is available
    useEffect(() => {
        document.title = 'Notifications | Zindua LMS';
    }, []);

    // When the status filter changes, reset to page 1 (fetch will happen via page effect)
    useEffect(() => {
        // Skip the initial mount run
        if (!statusFilterInitializedRef.current) {
            statusFilterInitializedRef.current = true;
            return;
        }
        setCurrentPage(1);
    }, [statusFilter]);

    const handleNotificationClick = async id => {
        const notification = notifications.find(n => n.id === id);
        if (notification?.is_read) return;

        setNotifications(prevNotifications =>
            prevNotifications.map(notif => (notif.id === id ? { ...notif, is_read: true } : notif))
        );

        try {
            if (!token) return;
            await api.markNotificationsRead(token, [id]);
        } catch (error) {
            console.error(`Failed to mark notification ${id} as read`, error);
            setNotifications(prevNotifications =>
                prevNotifications.map(notif => (notif.id === id ? { ...notif, is_read: false } : notif))
            );
            toast.error('Failed to mark notification as read.');
        }
    };

    const handleMarkAllAsRead = async () => {
        if (!user) return;
        try {
            if (!token) return;
            const unreadNotificationIds = notifications.filter(n => !n.is_read).map(n => n.id);
            if (unreadNotificationIds.length === 0) {
                toast.info('No unread notifications to mark as read.');
                return;
            }
            await api.markNotificationsRead(token, unreadNotificationIds);
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
            toast.success('All notifications marked as read.');
        } catch (error) {
            console.error('Failed to mark all notifications as read', error);
            toast.error('Failed to mark all notifications as read.');
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    const filteredNotifications = notifications
        .filter(notification => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'read') return notification.is_read;
            if (statusFilter === 'unread') return !notification.is_read;
            return true;
        })
        .sort((a, b) => parseNotificationDate(b.created_at).getTime() - parseNotificationDate(a.created_at).getTime());

    const isToday = someDate => {
        const today = new Date();
        return (
            someDate.getDate() === today.getDate() &&
            someDate.getMonth() === today.getMonth() &&
            someDate.getFullYear() === today.getFullYear()
        );
    };

    // Pagination control functions
    const goToPage = page => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const goToFirstPage = () => goToPage(1);
    const goToLastPage = () => goToPage(totalPages);
    const goToPreviousPage = () => goToPage(currentPage - 1);
    const goToNextPage = () => goToPage(currentPage + 1);

    const paginationProps = {
        currentPage,
        totalPages,
        goToFirstPage,
        goToLastPage,
        goToPreviousPage,
        goToNextPage,
        changePageSize: newSize => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
        },
        itemsPerPage,
        totalItems: totalNotificationsCount,
    };

    // This effect handles fetching data when the page number, status, or items-per-page changes.
    useEffect(() => {
        if (user) {
            fetchNotifications(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, statusFilter, itemsPerPage, user]);

    const recentNotifications = filteredNotifications.filter(notification => {
        const date = parseNotificationDate(notification.created_at);
        return date && isToday(date);
    });
    const earlierNotifications = filteredNotifications.filter(notification => {
        const date = parseNotificationDate(notification.created_at);
        return date && !isToday(date);
    });

    return (
        <DashboardWrapper>
            <div className="w-full">
                <div className="flex flex-col">
                    <div className="px-4 py-3">
                        <h2 className="font-bold">Notifications</h2>
                        <p className="text-neutral-700">Review your submissions, get feedback, and improve your workflow</p>
                    </div>
                    <Tabs
                        value={statusFilter}
                        onValueChange={setStatusFilter}
                        className="flex flex-col grow overflow-y-scroll w-full"
                    >
                        <div className="p-4 flex flex-col lg:flex-row justify-between items-start md:items-center gap-4 w-full">
                            <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-4 w-full">
                                <TabsList className="flex items-center gap-2 md:justify-between max-md:w-full">
                                    <TabsTrigger value="all" className="justify-center max-md:w-full capitalize">
                                        All
                                    </TabsTrigger>
                                    <TabsTrigger value="unread" className="justify-center max-md:w-full capitalize">
                                        Unread
                                    </TabsTrigger>
                                    <TabsTrigger value="read" className="justify-center max-md:w-full capitalize">
                                        Read
                                    </TabsTrigger>
                                </TabsList>
                                <div className="w-full md:self-end">
                                    {unreadCount > 0 && (
                                        <div className="w-full flex md:justify-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleMarkAllAsRead}
                                                className="w-full md:w-auto"
                                            >
                                                Mark all as read
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <TabsContent value="all" className="grow overflow-y-scroll p-4">
                            {loading ? (
                                <div className="flex flex-col gap-4">
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                </div>
                            ) : filteredNotifications.length > 0 ? (
                                <>
                                    {recentNotifications.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="mb-2">Recent</h3>
                                            {recentNotifications.map((notification, index) => (
                                                <NotificationItem
                                                    key={notification.id}
                                                    notification={notification}
                                                    onClick={handleNotificationClick}
                                                    isLast={index === recentNotifications.length - 1}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {earlierNotifications.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="mb-2">Earlier</h3>
                                            {earlierNotifications.map((notification, index) => (
                                                <NotificationItem
                                                    key={notification.id}
                                                    notification={notification}
                                                    onClick={handleNotificationClick}
                                                    isLast={index === earlierNotifications.length - 1}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <DataNotFoundSection
                                        title="No notifications match your filters."
                                        icon={<Bell size={30} className="text-primary mb-4 w-12 h-12" />}
                                    />
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="unread" className="grow overflow-y-scroll p-4">
                            {loading ? (
                                <div className="flex flex-col gap-4">
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                </div>
                            ) : filteredNotifications.length > 0 ? (
                                <>
                                    {recentNotifications.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold mb-2">Recent</h3>
                                            {recentNotifications.map((notification, index) => (
                                                <NotificationItem
                                                    key={notification.id}
                                                    notification={notification}
                                                    onClick={handleNotificationClick}
                                                    isLast={index === recentNotifications.length - 1}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {earlierNotifications.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold mb-2">Earlier</h3>
                                            {earlierNotifications.map((notification, index) => (
                                                <NotificationItem
                                                    key={notification.id}
                                                    notification={notification}
                                                    onClick={handleNotificationClick}
                                                    isLast={index === earlierNotifications.length - 1}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <DataNotFoundSection
                                        title="No notifications match your filters."
                                        icon={<Bell size={30} className="text-primary mb-4 w-12 h-12" />}
                                    />
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="read" className="grow overflow-y-scroll p-4">
                            {loading ? (
                                <div className="flex flex-col gap-4">
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                    <NotificationSkeleton />
                                </div>
                            ) : filteredNotifications.length > 0 ? (
                                <>
                                    {recentNotifications.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold mb-2">Recent</h3>
                                            {recentNotifications.map((notification, index) => (
                                                <NotificationItem
                                                    key={notification.id}
                                                    notification={notification}
                                                    onClick={handleNotificationClick}
                                                    isLast={index === recentNotifications.length - 1}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {earlierNotifications.length > 0 && (
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold mb-2">Earlier</h3>
                                            {earlierNotifications.map((notification, index) => (
                                                <NotificationItem
                                                    key={notification.id}
                                                    notification={notification}
                                                    onClick={handleNotificationClick}
                                                    isLast={index === earlierNotifications.length - 1}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <DataNotFoundSection
                                        title="No notifications match your filters"
                                        icon={<Bell size={30} className="text-primary mb-4 w-12 h-12" />}
                                    />
                                </div>
                            )}
                        </TabsContent>

                        {totalPages > 1 && (
                            <div className="p-4">
                                <PaginationControls
                                    pagination={paginationProps}
                                    totalItems={totalNotificationsCount}
                                    itemType="notifications"
                                />
                            </div>
                        )}
                    </Tabs>
                </div>
            </div>
        </DashboardWrapper>
    );
};
export default NotificationsPage;
